import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Edit3 } from 'lucide-react';
import type { Room } from '../types';

interface RoomRenameModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRename: (roomId: string, newLabel: string) => void;
  roomToRename: Room | null;
  existingRooms: Room[];
  existingLabels: Record<string, string>;
  isFullscreen?: boolean;
}

const PREDEFINED_LABELS = [
  { name: 'Bedroom', prefix: 'BR', default: 'BR1' },
  { name: 'Bathroom', value: 'BTH' },
  { name: 'Living Room', prefix: 'LR', default: 'LR1' },
  { name: 'Hallway', prefix: 'H', default: 'H1' },
  { name: 'Landing', prefix: 'L', default: 'L1' },
  { name: 'Kitchen/Diner', value: 'K/D' },
  { name: 'Kitchen', value: 'K' },
  { name: 'Water Closet', value: 'W/C' },
  { name: 'Cupboard', value: 'CPD' },
  { name: 'Dining Room', prefix: 'DR', default: 'DR1' },
  { name: 'Study', prefix: 'ST', default: 'ST1' },
  { name: 'Utility', prefix: 'UTL', default: 'UTL1' },
  { name: 'Conservatory', prefix: 'CON', default: 'CON1' },
];

const RoomRenameModal: React.FC<RoomRenameModalProps> = ({
  isOpen,
  onClose,
  onRename,
  roomToRename,
  existingRooms,
  existingLabels,
  isFullscreen = false,
}) => {
  const [newLabel, setNewLabel] = useState('');

  useEffect(() => {
    if (roomToRename && roomToRename.stableId) {
      // Get current label or fallback to room name
      const currentLabel = existingLabels[roomToRename.stableId] || roomToRename.name || '';
      setNewLabel(currentLabel);
    } else {
      setNewLabel('');
    }
  }, [roomToRename, existingLabels]);

  const handleSave = () => {
    if (roomToRename?.stableId && newLabel.trim()) {
      onRename(roomToRename.stableId, newLabel.trim());
      onClose();
    } else {
      console.error('🔧 EXTRUSION FIX: Cannot save room rename - missing stable ID or label:', {
        stableId: roomToRename?.stableId,
        label: newLabel.trim(),
        roomToRename
      });
    }
  };

  const handleCancel = () => {
    onClose();
  };

  const handlePredefinedClick = (selected: { name: string; prefix?: string; value?: string; default?: string }) => {
    if (selected.prefix) {
      let nextNumber = 1;
      let suggestedLabel = `${selected.prefix}${nextNumber}`;
      // Check against existing labels, not room names
      const existingLabelValues = Object.values(existingLabels);
      while (existingLabelValues.includes(suggestedLabel) && roomToRename?.stableId && existingLabels[roomToRename.stableId] !== suggestedLabel) {
        nextNumber++;
        suggestedLabel = `${selected.prefix}${nextNumber}`;
      }
      setNewLabel(suggestedLabel);
    } else {
      setNewLabel(selected.value || selected.default || 'New Room');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  if (!isOpen || !roomToRename || !roomToRename.stableId) {
    // 🔧 EXTRUSION FIX: Don't show modal if room has no stable ID
    if (isOpen && roomToRename && !roomToRename.stableId) {
      console.error('🔧 EXTRUSION FIX: Cannot rename room - no stable ID:', roomToRename);
    }
    return null;
  }

  const currentLabel = roomToRename.stableId ? existingLabels[roomToRename.stableId] || roomToRename.name : roomToRename.name;

  const modalContent = (
    <div className={`fixed inset-0 bg-gradient-to-br from-slate-900/80 via-slate-800/70 to-slate-900/80 backdrop-blur-sm flex items-center justify-center p-4 ${isFullscreen ? 'z-[10003]' : 'z-[999998]'}`}>
      <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl w-full max-w-lg border border-white/20">
        {/* Header */}
        <div className="relative overflow-hidden rounded-t-2xl">
          <div className="bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 p-6">
            <div className="flex items-center justify-between relative z-10">
              <div className="flex items-center space-x-3">
                <div className="bg-white/20 rounded-full p-2">
                  <Edit3 className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white mb-1">Edit Room Label</h2>
                  <p className="text-emerald-100 text-sm">
                    Current: <span className="font-medium">{currentLabel}</span>
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-white/80 hover:text-white hover:bg-white/10 rounded-full p-2 transition-all duration-200"
              >
                <X size={20} />
              </button>
            </div>
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-400/20 to-cyan-400/20 backdrop-blur-3xl"></div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6 bg-gradient-to-b from-gray-50/50 to-white">
          <div>
            <label className="flex items-center text-sm font-semibold text-gray-900 mb-2">
              <div className="w-2 h-2 bg-emerald-500 rounded-full mr-3"></div>
              New Room Label
            </label>
            <input
              type="text"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder="e.g., Bedroom 1, Living Room"
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500 transition-all duration-200 bg-white/50 backdrop-blur-sm text-gray-900 placeholder-gray-500"
              autoFocus
            />
          </div>

          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
              <div className="w-2 h-2 bg-teal-500 rounded-full mr-3"></div>
              Quick Suggestions
            </h3>
            <div className="grid grid-cols-2 gap-2">
              {PREDEFINED_LABELS.map((pLabel) => (
                <button
                  key={pLabel.name}
                  onClick={() => handlePredefinedClick(pLabel)}
                  className="px-3 py-2.5 text-sm bg-gradient-to-r from-gray-50 to-teal-50/30 text-gray-700 rounded-xl hover:from-teal-50 hover:to-teal-100/50 hover:shadow-md transition-all duration-200 border border-gray-200/50 hover:border-teal-300 font-medium text-left"
                >
                  <div className="truncate">
                    {pLabel.prefix ? `${pLabel.name}` : pLabel.name}
                  </div>
                  <div className="text-xs text-gray-500 truncate">
                    {pLabel.prefix ? `e.g. ${pLabel.prefix}1` : pLabel.value}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200/50 bg-gradient-to-r from-gray-50/50 to-white rounded-b-2xl">
          <button
            onClick={handleCancel}
            className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 shadow-sm hover:shadow-md"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!newLabel.trim()}
            className={`px-6 py-2.5 text-sm font-medium rounded-xl transition-all duration-200 flex items-center space-x-2 shadow-lg ${
              newLabel.trim()
                ? 'text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 hover:shadow-xl hover:scale-105 ring-2 ring-emerald-200/50'
                : 'text-gray-400 bg-gray-200 cursor-not-allowed'
            }`}
          >
            <Edit3 size={16} />
            <span>Update Label</span>
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default RoomRenameModal; 